import { zodResolver } from '@hookform/resolvers/zod'
import { Stack } from '@mui/joy'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  carUnderRepairSubStatusEnumOptions,
  OptionEnum,
} from '~/shared/config/constants'
import {
  CarStatusEnum,
  CarUnderRepairSubStatusEnum,
} from '~/shared/config/enums'
import { dateSchema, enumOptionSchema } from '~/shared/lib/schemas'
import { Button } from '~/shared/ui/Buttons'
import {
  AutocompleteInput,
  DatePickerInput,
  FormProvider,
  TextareaInput,
} from '~/shared/ui/Form'

const formRequiredSchema = z.object({
  comment: z.string().min(3),
})
const formSchema = z.object({
  comment: z.string().optional(),
})

const subStatusSchema = z.object({
  subStatus: enumOptionSchema(CarUnderRepairSubStatusEnum),
  plannedDateEndRepair: dateSchema,
})

// Тяжелый ремонт нельзя выбрать при смене статуса вручную, но статус
// продолжает отображаться в остальных местах (списки, фильтры, карточка авто).
const changeStatusSubStatusOptions = carUnderRepairSubStatusEnumOptions.filter(
  (option) => option.id !== CarUnderRepairSubStatusEnum.HEAVY_REPAIR,
)

export type FormValues = {
  comment?: string
  subStatus?:
    | CarUnderRepairSubStatusEnum
    | OptionEnum<CarUnderRepairSubStatusEnum>
    | null
  plannedDateEndRepair?: string
}

type FormProps = {
  onSuccess: (formValues: FormValues) => void
  onCancel: () => void
  mainStatus?: CarStatusEnum
  subStatus: OptionEnum<CarUnderRepairSubStatusEnum> | null
  isSubmitting: boolean
}

export function Form({
  mainStatus,
  subStatus,
  isSubmitting,
  onSuccess,
  onCancel,
}: FormProps) {
  const { t } = useTranslation()

  const isStatusUnderRepair = mainStatus === CarStatusEnum.UNDER_REPAIR
  const isStatusFree = mainStatus === CarStatusEnum.FREE

  const schema = useMemo(() => {
    if (isStatusUnderRepair) return formRequiredSchema.merge(subStatusSchema)
    if (isStatusFree) return formSchema

    return formRequiredSchema
  }, [isStatusFree, isStatusUnderRepair])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      subStatus,
    },
  })

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Stack spacing={2}>
        {isStatusUnderRepair && (
          <>
            <AutocompleteInput
              label='form.substatus'
              name='subStatus'
              placeholder='form.enter substatus'
              options={changeStatusSubStatusOptions}
              readOnly={isSubmitting}
            />
            <DatePickerInput
              label='form.planned tech. repair exit date'
              name='plannedDateEndRepair'
              placeholder='form.enter planned tech. repair exit date'
              readOnly={isSubmitting}
            />
          </>
        )}
        <TextareaInput
          label='form.comment'
          name='comment'
          placeholder='form.enter comment'
          sx={{
            height: '138px',
          }}
          readOnly={isSubmitting}
        />
      </Stack>
      <br />
      <Stack direction='row' justifyContent='space-between' spacing={2}>
        <Button
          variant='gray'
          onClick={onCancel}
          fullWidth
          disabled={isSubmitting}
        >
          {t('button.cancel')}
        </Button>
        <Button variant='brand' type='submit' fullWidth loading={isSubmitting}>
          {t('button.change status')}
        </Button>
      </Stack>
    </FormProvider>
  )
}
